import { Play, Pause, Radio, Clock } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { fmtUTC } from "@/lib/maritime";

const SPEEDS = [1, 10, 60];

export const PlaybackBar = ({
  range,
  t,
  mode,
  playing,
  speed,
  onScrub,
  onTogglePlay,
  onSpeed,
  onLive,
}) => {
  const start = range.start ? new Date(range.start).getTime() : Date.now() - 3600_000;
  const end = range.end ? new Date(range.end).getTime() : Date.now();
  const cur = mode === "live" ? end : t;

  return (
    <div
      data-testid="playback-bar"
      className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 w-[92%] max-w-4xl rounded-xl bg-slate-900/90 backdrop-blur-md border border-slate-800/90 p-3 shadow-2xl flex flex-col md:flex-row items-center gap-3"
    >
      <div className="flex items-center gap-2">
        <button
          data-testid="playback-play-pause-btn"
          onClick={onTogglePlay}
          className="flex h-9 w-9 items-center justify-center rounded-lg bg-cyan-500/15 border border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/25 transition-colors"
        >
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
        </button>
        <div className="flex rounded-lg border border-slate-700/60 overflow-hidden">
          {SPEEDS.map((s) => (
            <button
              key={s}
              data-testid={`playback-speed-${s}x`}
              onClick={() => onSpeed(s)}
              className="px-2 py-1.5 text-[11px] font-mono transition-colors"
              style={{
                background: speed === s && mode === "playback" ? "rgba(6,182,212,0.2)" : "transparent",
                color: speed === s && mode === "playback" ? "#22d3ee" : "#94a3b8",
              }}
            >
              {s}×
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 w-full flex items-center gap-3">
        <Slider
          data-testid="time-slider-scrubber"
          min={start}
          max={end}
          step={1000}
          value={[cur]}
          onValueChange={(val) => onScrub(val[0])}
          className="flex-1"
        />
      </div>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5 font-mono text-[11px] text-slate-300 tabular-nums whitespace-nowrap">
          <Clock className="h-3.5 w-3.5 text-slate-500" />
          <span data-testid="playback-timestamp">{fmtUTC(cur)}</span>
        </div>
        {mode === "live" ? (
          <div
            data-testid="live-status-indicator"
            className="flex items-center gap-1.5 px-1.5 py-1 text-[11px] font-mono uppercase tracking-wider text-emerald-400 whitespace-nowrap"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            Live
          </div>
        ) : (
          <button
            data-testid="playback-jump-live"
            onClick={onLive}
            className="flex items-center gap-1.5 rounded-lg border border-cyan-500/50 bg-cyan-500/15 px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-wider text-cyan-300 hover:bg-cyan-500/25 transition-colors whitespace-nowrap"
          >
            <Radio className="h-3.5 w-3.5" />
            Return to live
          </button>
        )}
      </div>
    </div>
  );
};

export default PlaybackBar;
